import {filter, unionBy, isEmpty} from 'lodash';
import fp from 'lodash/fp';
import req from 'shared/req';
import {PascalInstitutionType} from 'src/types/enums';

const subtextFields = ['street_address', 'city', 'state'];
const formatSchoolSubtext = fp.flow(fp.at(subtextFields), fp.compact, fp.join(', '));

export interface FormattedCommunityCollegeResponse {
  _id: string;
  text: string;
  ncesId: string;
  directoryInfoId: string;
  subtext: string;
  campuses: string;
  communityCollege: boolean;
  parentId: string;
  educationPhase: string;
  value: string;
  label: string;
}

const transformCollegeResults = (
  query,
  initialResults,
  results
): FormattedCommunityCollegeResponse[] => {
  const normalized = query.toLowerCase();
  const yourSchools = filter(
    initialResults,
    (result) => result.text.toLowerCase().indexOf(normalized) >= 0
  );

  const moreSchools = results.map((result) => {
    const fields = result.fields;
    const campuses =
      fields.campuses && !isEmpty(fields.campuses) ? JSON.parse(fields.campuses) : [];
    const isCommunityCollege = fields.community_college === 'true';

    return {
      _id: result.id,
      text: fields.name,
      ncesId: fields.nces_id,
      directoryInfoId: fields.directory_info_id,
      subtext: formatSchoolSubtext(fields),
      campuses,
      communityCollege: isCommunityCollege,
      parentId: isCommunityCollege ? fields.nces_id : result.id,
      educationPhase: isCommunityCollege
        ? PascalInstitutionType.COMMUNITY_COLLEGE
        : PascalInstitutionType.UNDERGRADUATE,

      // path
      value: fields.name,
      label: fields.name,
    };
  });

  return unionBy(yourSchools, moreSchools, '_id');
};

interface CommunityCollegeResponse {
  id: string;
  fields: {
    name: string;
    street_address: string;
    city: string;
    directory_info_id: string;
    nces_id: string;
    state: string;
    campuses: string;
    community_college: string;
  };
}

export async function collegeQueryResults(
  query: string,
  initialResults = [],
  communityCollege = false,
  loc: [lat: number, long: number] | undefined = undefined,
  state = ''
) {
  const data = {q: query};

  if (communityCollege) {
    data['community-college'] = true;
  }

  if (loc) {
    data['lat'] = loc[0];
    data['long'] = loc[1];
  }

  if (state) {
    data['state'] = state;
  }

  return req<CommunityCollegeResponse[]>({
    url: '/v1/nearest-colleges',
    method: 'get',
    data,
  }).then((results) => transformCollegeResults(query, initialResults, results));
}
